import {
  ArchiveRestore,
  Check,
  ChevronRight,
  Clock3,
  Database,
  Download,
  FileClock,
  KeyRound,
  LockKeyhole,
  RotateCcw,
  Settings,
  ShieldCheck,
  Trash2,
  UserRound
} from "lucide-react";
import { useState } from "react";
import { Button, Card, EmptyState, PageHeader, SectionHeading, StatusPill } from "../components/ui";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";

type Tab = "profile" | "privacy" | "audit" | "trash";

const actionLabels: Record<string, string> = {
  CREATE: "创建",
  UPDATE: "更新",
  COMPLETE: "完成",
  STATUS_CHANGE: "状态变化",
  GENERATE_DRAFT: "生成草稿",
  PUBLISH: "发布",
  SOFT_DELETE: "移入回收站",
  RESTORE: "恢复"
};

export function SettingsPage() {
  const { data, openModal, mutate, notify } = useApp();
  const [tab, setTab] = useState<Tab>("profile");
  const [currentTheme, setCurrentTheme] = useState(() => {
    try {
      return localStorage.getItem("eo_theme") ?? "";
    } catch {
      return "";
    }
  });
  if (!data) return null;

  const exportData = () => {
    const content = JSON.stringify(
      {
        exported_at: new Date().toISOString(),
        schema: "earth-online-v3",
        goals: data.goals,
        projects: data.projects,
        tasks: data.tasks,
        activity_events: data.events,
        skills: data.skills,
        reviews: data.reviews,
        contacts: data.contacts,
        customer_orders: data.customerOrders,
        assets: data.assets,
        financial_plans: data.financialPlans,
        transactions: data.transactions,
        audit_logs: data.audits
      },
      null,
      2
    );
    const url = URL.createObjectURL(new Blob([content], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `earth-online-backup-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    notify("结构化备份已导出", "success");
  };

  return (
    <div className="page page--settings">
      <PageHeader
        eyebrow="SETTINGS · 边界与可恢复性"
        title="设置与审计"
        description="边界清晰，空间才会长久安稳。"
      />

      <div className="settings-layout">
        <Card className="settings-nav">
          {[
            { id: "profile" as Tab, label: "个人空间", icon: UserRound },
            { id: "privacy" as Tab, label: "隐私与数据", icon: LockKeyhole },
            { id: "audit" as Tab, label: "审计日志", icon: FileClock },
            { id: "trash" as Tab, label: "回收站", icon: Trash2 }
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} className={tab === id ? "active" : ""} onClick={() => setTab(id as Tab)}>
              <Icon size={17} />
              <span>{label}</span>
              {id === "trash" && data.deleted.length > 0 && <b>{data.deleted.length}</b>}
              <ChevronRight size={15} />
            </button>
          ))}
        </Card>

        <div className="settings-content">
          {tab === "profile" && (
            <>
              <Card>
                <SectionHeading
                  title="个人空间"
                  description="称呼、时区和当前版本会影响首页呈现，不影响事实本身。"
                  action={<Button variant="secondary" size="sm" onClick={() => openModal("profile")}>编辑</Button>}
                />
                <div className="profile-settings">
                  <div className="profile-settings__avatar">{data.profile?.display_name?.slice(0, 1) || "我"}</div>
                  <div>
                    <strong>{data.profile?.display_name || "尚未设置称呼"}</strong>
                    <span>{data.profile?.current_version || "尚未发布个人版本"}</span>
                  </div>
                  <dl>
                    <div><dt>时区</dt><dd>{data.profile?.timezone || "Asia/Shanghai"}</dd></div>
                    <div><dt>空间类型</dt><dd>单 Owner · 本地私密</dd></div>
                    <div><dt>最后更新</dt><dd>{data.profile?.updated_at ? formatDateTime(data.profile.updated_at) : "尚未设置"}</dd></div>
                  </dl>
                </div>
              </Card>
              <Card>
                <SectionHeading title="主题配色" description="选一套喜欢的配色，整个界面即时切换。" />
                <div className="theme-picker">
                  {[
                    { id: "", name: "深海极光", colors: ["#101a2a", "#4fa3e8", "#e3effa"], hint: "默认" },
                    { id: "galaxy", name: "星云紫蓝", colors: ["#151230", "#8b7cf7", "#ece9fb"], hint: "神秘" },
                    { id: "sky", name: "清晨天空", colors: ["#122940", "#5a9fe0", "#e5f0fa"], hint: "清新" },
                    { id: "tech", name: "科技青蓝", colors: ["#0b2830", "#18b8c8", "#dcf2f5"], hint: "极客" },
                    { id: "green", name: "经典绿", colors: ["#151814", "#9dbc5f", "#f0f4ea"], hint: "怀旧" }
                  ].map((theme) => (
                    <button
                      key={theme.id || "default"}
                      className={`theme-swatch ${currentTheme === theme.id ? "active" : ""}`}
                      onClick={() => {
                        setCurrentTheme(theme.id);
                        try {
                          if (theme.id) localStorage.setItem("eo_theme", theme.id);
                          else localStorage.removeItem("eo_theme");
                        } catch {
                          /* ignore */
                        }
                        if (theme.id) document.documentElement.setAttribute("data-theme", theme.id);
                        else document.documentElement.removeAttribute("data-theme");
                      }}
                    >
                      <span
                        className="theme-swatch__bar"
                        style={{ background: `linear-gradient(135deg, ${theme.colors[0]}, ${theme.colors[1]})` }}
                      />
                      <strong>{theme.name}</strong>
                      <small>{theme.hint}</small>
                    </button>
                  ))}
                </div>
              </Card>
              <Card>
                <SectionHeading title="系统状态" description="核心功能不依赖外部 AI 服务。" />
                <div className="system-status-list">
                  <StatusRow label="本地数据库" detail="SQLite · 事务与 WAL 已启用" ok />
                  <StatusRow label="事实审计" detail={`${data.audits.length} 条可查看日志`} ok />
                  <StatusRow label="帝王引擎" detail={data.meta?.aiAvailable ? "已连接 DeepSeek · 推演基于你的真实数据" : "本地规则核心 · 推演基于你的真实数据"} ok={Boolean(data.meta?.aiAvailable)} />
                  <StatusRow label="外部 AI 服务" detail={data.meta?.aiAvailable ? "DeepSeek 已配置 · 数据用于生成建议" : "未配置 · 数据不出服务器"} ok={Boolean(data.meta?.aiAvailable)} />
                  <StatusRow label="自动化运行器" detail="本地交互版暂未启用定时调度" ok={false} />
                </div>
              </Card>
            </>
          )}

          {tab === "privacy" && (
            <>
              <Card>
                <SectionHeading title="隐私边界" description="当前版本只服务单个本地 Owner。" />
                <div className="privacy-settings">
                  <PrivacyRow icon={<UserRound size={18} />} title="Owner 空间隔离" detail="所有业务记录绑定本地 Owner；没有团队成员或公开链接。" />
                  <PrivacyRow icon={<KeyRound size={18} />} title="服务密钥" detail="客户端与仓库不包含服务角色 token、AI 密钥或数据库管理凭证。" />
                  <PrivacyRow icon={<ShieldCheck size={18} />} title="高风险操作" detail="金额、删除、发布与技能升级均需明确确认并写入审计。" />
                </div>
              </Card>
              <Card>
                <SectionHeading
                  title="数据控制"
                  description="导出包含可读的结构化事实与审计，不包含本地草稿。"
                  action={<Button onClick={exportData} icon={<Download size={15} />}>导出备份</Button>}
                />
                <div className="data-control">
                  <Database size={24} />
                  <div>
                    <strong>你的事实数据可以带走</strong>
                    <p>导出当前目标、项目、任务、事件、技能、复盘、工作、财务与审计记录。</p>
                  </div>
                </div>
              </Card>
            </>
          )}

          {tab === "audit" && (
            <Card>
              <SectionHeading title="审计日志" description="谁在何时对哪个实体做了什么。" />
              {data.audits.length ? (
                <div className="audit-list">
                  {data.audits.map((audit) => (
                    <article key={audit.id}>
                      <span><FileClock size={16} /></span>
                      <div>
                        <strong>{actionLabels[audit.action] ?? audit.action}</strong>
                        <small>{audit.entity_type} · {audit.entity_id.slice(0, 8)}</small>
                      </div>
                      <time>{formatDateTime(audit.created_at)}</time>
                    </article>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={<FileClock size={22} />}
                  title="还没有关键操作"
                  description="创建或更新真实记录后，审计轨迹会出现在这里。"
                  compact
                />
              )}
            </Card>
          )}

          {tab === "trash" && (
            <Card>
              <SectionHeading
                title="回收站"
                description="软删除记录可恢复，关联事实不会被静默覆盖。"
              />
              {data.deleted.length ? (
                <div className="trash-list">
                  {data.deleted.map((item) => (
                    <article key={`${item.entity_type}-${item.id}`}>
                      <span><Trash2 size={16} /></span>
                      <div><strong>{item.label}</strong><small>{item.entity_type} · 删除于 {formatDateTime(item.deleted_at)}</small></div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => void mutate(() => api.restore(item.entity_type, item.id), "记录及其关联已恢复")}
                        icon={<ArchiveRestore size={14} />}
                      >
                        恢复
                      </Button>
                    </article>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={<Trash2 size={22} />}
                  title="回收站是空的"
                  description="被删除的项目、任务和记录会先进入这里，而不是立即消失。"
                  compact
                />
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusRow({ label, detail, ok }: { label: string; detail: string; ok: boolean }) {
  return (
    <div>
      <span className={ok ? "status-ok" : "status-warn"}>{ok ? <Check size={14} /> : <Clock3 size={14} />}</span>
      <div><strong>{label}</strong><small>{detail}</small></div>
      <StatusPill status={ok ? "ACTIVE" : "WATCH"}>{ok ? "正常" : "未配置"}</StatusPill>
    </div>
  );
}

function PrivacyRow({ icon, title, detail }: { icon: React.ReactNode; title: string; detail: string }) {
  return (
    <div>
      <span>{icon}</span>
      <div><strong>{title}</strong><p>{detail}</p></div>
      <StatusPill status="ACTIVE">已启用</StatusPill>
    </div>
  );
}
